import { useEffect, useState } from 'react';
import { formatPrice, type Squad, type SquadValidation } from '@fantasy/types';
import { Link } from '@tanstack/react-router';
import { api, ApiRequestError, type LivePlayerPoints, type OfficialGameweekPoints } from '../api.ts';
import { estimateSquadPoints } from '../lib/estimateSquadPoints.ts';
import { resolveGameweekPoints } from '../lib/gameweekPoints.ts';
import { useSquad } from '../state/useSquad.ts';

/**
 * The slim bar under the masthead (T-048) — the loaded squad's name,
 * gameweek, money in the bank and this gameweek's points stay visible no
 * matter which route is active, and "Switch squad" reaches another saved
 * squad without leaving it. This is deliberately not a second "Saved
 * squads" list — the full one still lives on the squad route
 * (`SquadLibrary`); this is just enough to switch without navigating there
 * first.
 */
export function SquadContextBar() {
  const { squad, loadFromLibrary } = useSquad();
  const [livePoints, setLivePoints] = useState<Map<number, LivePlayerPoints> | null>(null);
  const [official, setOfficial] = useState<OfficialGameweekPoints | null>(null);

  // Fetched here, not just left to SquadView's own "Live points" button —
  // gameweek and bank are already shown unconditionally in this bar, and
  // points is the third figure a manager expects alongside them, not one
  // gated behind a click on a different page. Cleared and re-fetched on
  // every squad switch so a previous squad's total can't linger under a
  // new one's name.
  useEffect(() => {
    setLivePoints(null);
    setOfficial(null);
    if (!squad) return;
    let cancelled = false;
    api
      .getLivePoints(squad.id)
      .then(({ points, official: fetchedOfficial }) => {
        if (cancelled) return;
        setLivePoints(new Map(points.map((p) => [p.fplId, p])));
        setOfficial(fetchedOfficial ?? null);
      })
      .catch(() => {
        // Not fatal — the bar just omits the points figure; SquadView's own
        // "Live points" button (with its own error message) is still there.
      });
    return () => {
      cancelled = true;
    };
  }, [squad?.id]);

  if (!squad) {
    return (
      <section className="context-bar context-bar--empty" aria-label="No squad loaded">
        <p className="hint">
          No squad loaded yet — <Link to="/squad">load or import one</Link> to get started.
        </p>
      </section>
    );
  }

  // Both this bar and ThisWeek resolve the same way — see gameweekPoints.ts.
  const estimated = estimateSquadPoints(squad, livePoints);
  const { points, isEstimate } = resolveGameweekPoints(official, estimated);

  return (
    <section className="context-bar" aria-label="Loaded squad">
      <div className="context-bar-info">
        <span className="context-bar-name">{squad.name}</span>
        <span className="context-bar-stat">Gameweek {squad.gameweek}</span>
        <span className="context-bar-stat">{formatPrice(squad.bank)} in the bank</span>
        {points !== null && (
          <span className="context-bar-stat">
            {points} pts
            {/* Discoverable, not shouty — a short suffix, not a sentence
             * explaining why: this squad's total is this app's estimate,
             * not FPL's own reported figure. */}
            {isEstimate && <span className="text-faint"> (estimate)</span>}
          </span>
        )}
      </div>
      <SquadSwitcher currentId={squad.id} onSwitch={loadFromLibrary} />
    </section>
  );
}

interface SquadSwitcherProps {
  currentId: string;
  onSwitch: (squad: Squad, validation: SquadValidation) => void;
}

/** Fetches the account's saved squads lazily, on first open, so a visit that
 * never switches squads costs nothing. */
function SquadSwitcher({ currentId, onSwitch }: SquadSwitcherProps) {
  const [open, setOpen] = useState(false);
  const [squads, setSquads] = useState<Squad[] | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function onToggle() {
    setOpen((wasOpen) => !wasOpen);
    if (squads !== null) return;
    api
      .listSquads()
      .then(({ squads: loaded }) => setSquads(loaded))
      .catch(() => setError('Could not load your saved squads.'));
  }

  async function onSelect(id: string) {
    setLoadingId(id);
    setError(null);
    try {
      const { squad: loaded, validation } = await api.getSquad(id);
      onSwitch(loaded, validation);
      setOpen(false);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Could not load that squad.');
    } finally {
      setLoadingId(null);
    }
  }

  const others = (squads ?? []).filter((s) => s.id !== currentId);

  return (
    <div className="context-bar-switch">
      <button type="button" className="ghost small" aria-expanded={open} onClick={onToggle}>
        Switch squad
      </button>

      {open && (
        <ul className="context-bar-switch-list list-reset">
          {squads === null && <li className="hint">Loading…</li>}
          {squads !== null && others.length === 0 && <li className="hint">No other saved squads.</li>}
          {others.map((other) => (
            <li key={other.id}>
              <button
                type="button"
                className="ghost small"
                onClick={() => onSelect(other.id)}
                disabled={loadingId === other.id}
                aria-busy={loadingId === other.id}
              >
                {other.name} · Gameweek {other.gameweek}
              </button>
            </li>
          ))}
          {error && <li className="hint text-bad">{error}</li>}
        </ul>
      )}
    </div>
  );
}
